import type {
  LoginResponseData,
  Maybe,
  Response,
  StoreThis,
  User,
} from '@/types'
import type { LoginFormType, ToastState } from '@/types/ui'

import { computed, ref } from 'vue'
import { handleErrors } from '@/helpers'

import { useRouter } from 'vue-router'

import { defineStore } from 'pinia'
import { useUiStore } from '@/stores/ui'

const resource = 'auth'

export const useAuthStore = defineStore(resource, () => {
  const ui = useUiStore()
  /**
   * Первоначально authToken заполняется в хранилище ui, если обнаружен в localStorage
   * Хранилище ui в первый раз запускается в TheAppLayout (а в uiStore инициализируется authStore)
   * */
  const authToken = ref<Maybe<string>>(null)
  const authorized = ref(false)

  const currentUser = ref<Maybe<User>>(null)
  const currentRole = computed(
    () => currentUser.value?.roles?.[0]?.code ?? null
  )

  const isLoading = ref(false)

  async function login(this: StoreThis, payload: LoginFormType) {
    let error = false
    let validationErrors: Record<string, string[]> = {}
    let otherError = ''

    isLoading.value = true

    try {
      const {
        data: { success, errors, data },
      }: Response<LoginResponseData> = await this.rest.post(
        `/m/${resource}/login`,
        payload
      )

      if (!success && errors) {
        validationErrors = errors.validation ?? {}
        otherError = errors.other ?? ''

        throw Error(handleErrors(errors))
      }

      authToken.value = data.token
      currentUser.value = data.user

      await setAuthHeader.call(this)

      // authorized.value = true
    } catch (e: any) {
      error = true
      console.error('[login]', resource, e)

      const errors = e?.response?.data?.errors
      if (errors) {
        validationErrors = errors.validation ?? {}
        otherError = errors.other ?? ''
      }
    } finally {
      isLoading.value = false
    }

    return { error, validationErrors, otherError }
  }

  async function resetPassword(this: StoreThis, email: string) {
    const options: Partial<ToastState> = {}
    let error = false
    let validationErrors: Record<string, string[]> = {}
    let otherError = ''

    isLoading.value = true

    try {
      const {
        data: { success, errors },
      }: Response<any> = await this.rest.post(`/m/${resource}/reset`, { email })

      if (!success && errors) {
        validationErrors = errors.validation ?? {}
        otherError = errors.other ?? ''

        throw Error(handleErrors(errors))
      }
    } catch (e: any) {
      error = true
      console.error('[reset]', resource, e)

      options.text = 'Сбросить пароль не удалось'
    } finally {
      isLoading.value = false

      if (options.text) ui.showToast(options)
    }

    return { error, validationErrors, otherError }
  }

  const router = useRouter()

  async function logout(this: StoreThis) {
    authToken.value = null
    currentUser.value = null

    await setAuthHeader.call(this)

    authorized.value = false

    await router.push({ name: 'manager-login' })
  }

  async function setAuthHeader(this: StoreThis) {
    this.setToken(authToken.value)
  }

  return {
    authToken,
    authorized,

    currentUser,
    currentRole,

    isLoading,

    login,
    logout,
    resetPassword,

    setAuthHeader,
  }
})
